import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FaBook, FaProjectDiagram, FaCalculator, FaMoneyBillWave, FaClipboardCheck, FaDraftingCompass, FaInfoCircle, FaSearch } from 'react-icons/fa';

const MermaidDiagram = ({ code }) => {
    const ref = useRef(null);

    useEffect(() => {
        if (window.mermaid && ref.current) {
            // Reset for re-renders - ensure we have the source code with newlines
            ref.current.removeAttribute('data-processed');
            // Explicitly set innerHTML to code to ensure fresh start
            ref.current.innerHTML = code;
            
            try {
                window.mermaid.init(undefined, ref.current);
            } catch (e) {
                console.error("Mermaid init error:", e);
            }
        }
    }, [code]);

    return (
        <div ref={ref} className="mermaid bg-gray-50 p-4 rounded border border-gray-200 overflow-x-auto text-center my-4">
            {code}
        </div>
    );
};

const TrainingDocs = () => {
    const [activeTab, setActiveTab] = useState('overview');
    const [enabledPlays, setEnabledPlays] = useState([]);
    const [loading, setLoading] = useState(true);

    const BASE_URL = process.env.REACT_APP_BE_MAIN_URL;
    const token = sessionStorage.getItem("userToken");

    useEffect(() => {
        if (window.mermaid) {
            window.mermaid.initialize({
                startOnLoad: true,
                theme: 'base',
                securityLevel: 'loose',
                themeVariables: {
                    primaryColor: '#1e5045',
                    primaryTextColor: '#ffffff',
                    primaryBorderColor: '#1e5045',
                    lineColor: '#1e5045',
                    secondaryColor: '#f26522',
                    tertiaryColor: '#f6f4ed',
                    fontFamily: 'ui-sans-serif, system-ui, sans-serif'
                }
            });
        }
    }, []);

    useEffect(() => {
        const fetchEnabledPlays = async () => {
            try {
                const res = await axios.get(`${BASE_URL}/user/admin/org/plays/`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const enabled = (res.data.enabled_plays || []).filter(p => p !== 'supply_chain_v1');
                setEnabledPlays(enabled);
            } catch (err) {
                console.error("Error fetching enabled plays", err);
                // Fallback: show all if we can't fetch status
                setEnabledPlays(['tariff_v1', 'veeco_drawing_checker_v2', 'cad_extract_v1']);
            } finally {
                setLoading(false);
            }
        };

        fetchEnabledPlays();
    }, [BASE_URL, token]);

    const isPlayEnabled = (playId) => {
        if (playId === 'checker') {
            return enabledPlays.some(p => p.includes('drawing_checker'));
        }
        return enabledPlays.includes(playId);
    };

    const allTabs = [
        { id: 'overview', label: 'Overview', icon: <FaInfoCircle />, alwaysShow: true },
        { id: 'flow', label: 'User Flow', icon: <FaProjectDiagram />, alwaysShow: true },
        { id: 'tariff', label: 'Tariff Analysis', icon: <FaCalculator />, playId: 'tariff_v1' },
        { id: 'checker_v2', label: 'Checker V2', icon: <FaDraftingCompass />, playId: 'veeco_drawing_checker_v2' },
        { id: 'drawing_atlas', label: 'Drawing Atlas', icon: <FaSearch />, playId: 'cad_extract_v1' }
    ];

    const tabs = allTabs.filter(tab => tab.alwaysShow || isPlayEnabled(tab.playId));

    if (loading) {
        return (
            <div className="flex h-screen items-center justify-center bg-gray-50">
                <div className="text-gray-500 text-xl font-medium">Loading training modules...</div>
            </div>
        );
    }

    const playDetails = {
        'tariff_v1': {
            name: 'Tariff Analysis',
            role: 'Trade Compliance Officer & Materials Scientist',
            value: 'Automates HTS classification & calculates total landed cost. Includes AI weight estimation (agentic specialist pipeline).'
        },
        'veeco_drawing_checker_v2': {
            name: 'Checker V2',
            role: 'Senior Quality Control Manager (Vision-Native)',
            value: 'Vision-native drawing validation using SourceOptima AI agentic vision with code execution for annotation, review, and conditional re-annotation.'
        },
        'cad_extract_v1': {
            name: 'Drawing Atlas',
            role: 'Senior Engineering Data Analyst',
            value: 'Full engineering record extraction from CAD drawings using SourceOptima Agentic AI with 8-stratum adversarial debate framework.'
        }
    };

    const renderContent = () => {
        switch (activeTab) {
            case 'overview':
                return (
                    <div className="space-y-6">
                        <div className="border-b border-gray-200 pb-4">
                            <h2 className="text-2xl font-bold text-gray-800">SourceOptima Analysis Plays</h2>
                            <p className="text-gray-700 mt-2">
                                SourceOptima converts static engineering drawings into actionable business intelligence. 
                                Instead of manually reviewing PDFs, you assign "AI Experts" (or Plays) to analyze your files. 
                                Each play acts as a specialized virtual employee—from a Trade Compliance Officer to a Senior Value Engineer.
                            </p>
                        </div>
                        
                        <div className="flex justify-center my-6 bg-gray-50 p-4 rounded-xl border border-gray-200">
                            <div className="text-center">
                                <img src="/training_screenshots/1_org_chart.png" alt="Organization Chart" className="border rounded-lg shadow-lg max-h-96 mx-auto mb-2" />
                                <p className="text-sm text-gray-500 italic">Figure 1: Organization & Space selection allows teams to collaborate securely.</p>
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-sm">
                                <thead className="bg-[#1E5045] text-white">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Play Name</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">The "AI Expert" Role</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Business Value</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 text-sm">
                                    {tabs.filter(t => !t.alwaysShow).map(tab => {
                                        const details = playDetails[tab.playId];
                                        if (!details) return null;
                                        return (
                                            <tr key={tab.id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 font-bold text-gray-900">{details.name}</td>
                                                <td className="px-6 py-4">{details.role}</td>
                                                <td className="px-6 py-4">{details.value}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                );
            case 'flow':
                return (
                    <div className="space-y-8">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-800 mb-4">User Interaction Flow</h2>
                            <p className="text-gray-600 mb-6">The platform follows a simple "Upload → Select → Analyze" workflow. The complexity of reading the drawing and applying rules is handled entirely by the AI.</p>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 my-6">
                                <div>
                                    <div className="bg-white p-2 border rounded-lg shadow-sm h-full">
                                        <h4 className="font-semibold text-center mb-2 bg-gray-100 py-1 rounded">1. Select Project</h4>
                                        <img src="/training_screenshots/2_project_list.png" alt="Project Selection" className="border rounded w-full" />
                                        <p className="text-xs text-gray-500 mt-2 px-2">Choose your project folder to access uploaded engineering drawings.</p>
                                    </div>
                                </div>
                                <div>
                                    <div className="bg-white p-2 border rounded-lg shadow-sm h-full">
                                        <h4 className="font-semibold text-center mb-2 bg-gray-100 py-1 rounded">2. Workspace & Analysis</h4>
                                        <img src="/training_screenshots/3_workspace_view.png" alt="Workspace Analysis" className="border rounded w-full" />
                                        <p className="text-xs text-gray-500 mt-2 px-2">Select files, choose an "Analysis Play", and click Run. Results appear in the File Detail view.</p>
                                    </div>
                                </div>
                            </div>

                            <MermaidDiagram code={`
graph TD
    A["Start"] --> B["Login & Select Space"]
    B --> C["Open Project Folder"]
    C --> D["Select Drawings to Analyze"]
    
    subgraph Analysis Configuration
    D --> E{"Choose Play"}
    E -->|Tariff| F["Compliance Analysis"]
    E -->|Cost| G["Value Engineering"]
    E -->|Design| H["Gatekeeper Review"]
    end
    
    F --> I["Run Analysis"]
    G --> I
    H --> I
    
    I --> J["AI Process & Reasoning"]
    J --> K["View Results & Report"]
                            `} />
                        </div>
                    </div>
                );
            case 'tariff':
                return (
                    <div className="space-y-6">
                        <div className="bg-blue-50 border-l-4 border-blue-600 p-4">
                            <h2 className="text-2xl font-bold text-gray-800">Play 1: Tariff Analysis</h2>
                            <p className="text-gray-700 font-medium mt-1">AI Role: Trade Compliance Officer & Materials Scientist</p>
                        </div>
                        
                        <p className="text-gray-700">
                            This play automates the complex task of customs classification. It reads the separate BOM, title block, and notes to find the materials and manufacturing method, then references the official <strong>Harmonized Tariff Schedule (HTS)</strong>.
                        </p>

                        <MermaidDiagram code={`
graph TD
    A["PDF Drawing"] --> B["AI: Extract Material & Specs"]
    B --> C{"Weight Explicitly Stated?"}
    C -- Yes --> D["Use Listed Weight"]
    C -- No --> E["Calculated: Geometry x Density"]
    D --> F["AI: Determine HTS Code"]
    E --> F
    F --> G["Check Section 301 'China List'"]
    F --> H["Check Section 232 'Steel/Alum'"]
    G --> I["Calculate Total Landed Cost"]
    H --> I
                        `} />

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                                    <h3 className="font-bold text-gray-800 mb-2 border-b pb-2">What the AI Does</h3>
                                    <ul className="list-disc pl-5 space-y-2 text-gray-600 text-sm">
                                        <li><strong>Reads Technical Data:</strong> Extracts material (e.g., "Aluminum 6061-T6") and specs.</li>
                                        <li><strong>Finds or Calculates Weight:</strong> It searches for an extracted weight first. If missing, it calculates it based on volume and density—critical for logistics costs.</li>
                                        <li><strong>Classifies Goods:</strong> Determines the 10-digit HTS code based on form and function.</li>
                                        <li><strong>Applies Trade Rules:</strong> Checks critical tariffs like Section 301 (China) and Section 232 (Steel/Aluminum).</li>
                                    </ul>
                                </div>

                                <div className="bg-blue-50 p-4 rounded-lg">
                                    <h3 className="text-sm font-bold text-blue-800 uppercase tracking-wide mb-2">Assembly Mode</h3>
                                    <p className="text-sm text-blue-800">
                                        For complex products, check <strong>"Treat as Assembly"</strong>. The AI will roll up costs from all child parts into the Top-Level Parent drawing to give a total landed cost for the finished good.
                                    </p>
                                </div>
                            </div>
                            
                            <div>
                                <h4 className="font-semibold text-gray-700 mb-2">Workspace Controls</h4>
                                <img src="/training_screenshots/3_workspace_view.png" alt="Workspace" className="border rounded shadow-md w-full" />
                            </div>
                        </div>
                    </div>
                );
            case 'checker_v2':
                return (
                    <div className="space-y-6">
                        <div className="bg-violet-50 border-l-4 border-violet-600 p-4">
                            <h2 className="text-2xl font-bold text-gray-800">Play: Drawing Checker V2</h2>
                            <p className="text-gray-700 font-medium mt-1">AI Role: Senior Quality Control Manager (Enhanced)</p>
                        </div>

                        <p className="text-gray-700">
                            V2 builds on the <strong>Consolidated (Both)</strong> checker with six new capabilities. It runs the same parallel Veeco + General agent pipeline, then adds post-processing intelligence on top.
                        </p>

                        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 text-sm">
                            <h3 className="font-bold text-yellow-800 mb-1">V1 vs V2 — What Changed?</h3>
                            <p className="text-yellow-900">
                                V1 remains available and unchanged. V2 is a separate play you select from the dropdown. Both can run on the same file independently.
                            </p>
                        </div>

                        <MermaidDiagram code={`
graph TD
    A["PDF Drawing"] --> B{"Parallel Execution"}
    B --> C["Agent A: Veeco Specialist"]
    B --> D["Agent B: Industry Specialist"]
    C --> E["Veeco Issues"]
    D --> F["General/DFM Issues"]
    E --> G["Consolidator AI"]
    F --> G
    G --> H["Confidence Recalibration"]
    H --> J["Quality Report Ready"]
    H --> K["SourceOptima AI Vision Annotation"]
    K --> L["Annotated Drawings Available"]
    M["User Profile"] -.-> B
    J -.-> N["Feedback Loop"]
    N -.-> M
                        `} />

                        <h3 className="text-lg font-bold text-gray-800 mt-4">Core Features (from V1)</h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                                <h4 className="font-bold text-sm mb-1" style={{color: '#1e5045'}}>Dual-Agent Analysis</h4>
                                <p className="text-xs text-gray-600">
                                    Two specialized SourceOptima AI agents run in parallel — one focused on <strong>Veeco internal standards</strong> (PRS179, 3EN7347, title block compliance) and one on <strong>general industry standards</strong> (ASME Y14.5, ISO, GD&T, DFM).
                                </p>
                            </div>

                            <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                                <h4 className="font-bold text-sm mb-1" style={{color: '#1e5045'}}>AI Consolidation</h4>
                                <p className="text-xs text-gray-600">
                                    A consolidator AI merges findings from both agents, deduplicates overlapping issues, and produces a single unified issue list with consistent formatting.
                                </p>
                            </div>

                            <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                                <h4 className="font-bold text-sm mb-1" style={{color: '#1e5045'}}>Severity Classification</h4>
                                <p className="text-xs text-gray-600">
                                    Every issue is classified as <strong className="text-red-600">CRITICAL</strong>, <strong className="text-orange-600">MAJOR</strong>, <strong className="text-green-600">MINOR</strong>, or <strong className="text-gray-500">INFO</strong> — helping you prioritize what to fix first.
                                </p>
                            </div>

                            <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                                <h4 className="font-bold text-sm mb-1" style={{color: '#1e5045'}}>Standard References</h4>
                                <p className="text-xs text-gray-600">
                                    Issues cite specific standards (ASME Y14.5, ISO 2768, Veeco specs) so you can trace each finding back to its source requirement.
                                </p>
                            </div>

                            <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                                <h4 className="font-bold text-sm mb-1" style={{color: '#1e5045'}}>Full HTML Quality Report</h4>
                                <p className="text-xs text-gray-600">
                                    A comprehensive, printable report with executive summary, issue cards, severity breakdown, and drawing preview — viewable in a new browser tab.
                                </p>
                            </div>

                            <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                                <h4 className="font-bold text-sm mb-1" style={{color: '#1e5045'}}>Multi-Page PDF Support</h4>
                                <p className="text-xs text-gray-600">
                                    Handles multi-page engineering drawings. Each page is processed and all findings are consolidated into a single report.
                                </p>
                            </div>

                            <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                                <h4 className="font-bold text-sm mb-1" style={{color: '#1e5045'}}>Excel Export</h4>
                                <p className="text-xs text-gray-600">
                                    Download all findings as a structured Excel spreadsheet (.xlsx) for offline review, tracking, or integration with your existing quality workflows.
                                </p>
                            </div>
                        </div>

                        <h3 className="text-lg font-bold text-gray-800 mt-4">The 7 New Features in V2</h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                                <h4 className="font-bold text-sm mb-1" style={{color: '#1e5045'}}>1. Confidence Labels</h4>
                                <p className="text-xs text-gray-600">
                                    V1 shows raw confidence percentages (e.g., "85% conf") that are not calibrated. V2 replaces them with evidence-based labels:
                                </p>
                                <ul className="list-disc pl-4 text-xs text-gray-600 mt-1">
                                    <li><strong className="text-green-700">Verified</strong> — cites a standard AND flagged by both agents</li>
                                    <li><strong className="text-blue-700">Likely</strong> — cites a standard OR flagged by both agents</li>
                                    <li><strong className="text-gray-500">Possible</strong> — single agent, no standard cited</li>
                                </ul>
                            </div>

                            <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                                <h4 className="font-bold text-sm mb-1" style={{color: '#1e5045'}}>2. User Personalization</h4>
                                <p className="text-xs text-gray-600">
                                    V2 learns what matters to you. Every time you rate an issue or drill down into one, the system records that signal and uses it to adjust future analyses.
                                </p>
                                <ul className="list-disc pl-4 text-xs text-gray-600 mt-2 space-y-1">
                                    <li><strong>Categories you find useful</strong> get more detailed rationale and fixes</li>
                                    <li><strong>Categories you dismiss</strong> get filtered at lower severities (CRITICAL/MAJOR are never filtered)</li>
                                    <li><strong>Topics you drill into</strong> get more specific standard references</li>
                                </ul>
                                <p className="text-xs text-gray-400 mt-2">First run: no personalization. After a few reviews with feedback, results start adapting. No setup required.</p>
                            </div>

                            <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                                <h4 className="font-bold text-sm mb-1" style={{color: '#1e5045'}}>3. Feedback Loop</h4>
                                <p className="text-xs text-gray-600">
                                    Each issue card shows three buttons: <strong>👍 Useful</strong>, <strong>😐 Neutral</strong>, <strong>👎 Not Useful</strong>. Feedback is saved instantly — no submit button needed.
                                </p>
                                <p className="text-xs text-gray-600 mt-1">
                                    Your ratings are tallied by category. Once a category has enough ratings, the system classifies it as high-value or low-value and adjusts accordingly. Rating just a few issues per analysis is enough.
                                </p>
                            </div>

                            <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                                <h4 className="font-bold text-sm mb-1" style={{color: '#1e5045'}}>4. Drill-Down</h4>
                                <p className="text-xs text-gray-600">
                                    Click <strong>🔍 Drill Down</strong> on any issue for a focused deep-dive: root cause, step-by-step fix, standard references, and impact assessment.
                                </p>
                            </div>

                            <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                                <h4 className="font-bold text-sm mb-1" style={{color: '#1e5045'}}>5. Additive Manufacturing</h4>
                                <p className="text-xs text-gray-600">
                                    V2 adds AM-specific checks: build orientation, support structures, layer adhesion, powder removal, and post-processing. These only fire for AM-relevant parts.
                                </p>
                            </div>

                            <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                                <h4 className="font-bold text-sm mb-1" style={{color: '#1e5045'}}>6. Agentic Vision Annotation</h4>
                                <p className="text-xs text-gray-600">
                                    V1 uses OCR bounding boxes (often misaligned). V2 uses SourceOptima AI vision to locate issues on the drawing, then draws deterministic annotations with consistent colors per severity.
                                </p>
                            </div>

                            <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                                <h4 className="font-bold text-sm mb-1" style={{color: '#1e5045'}}>7. Component Context</h4>
                                <p className="text-xs text-gray-600">
                                    Describe what a part does (e.g., <em>"Wafer chuck that holds 200mm substrates at 600°C in a CVD chamber"</em>) and the analysis adapts — flagging thermal, material, and environment-specific issues it would otherwise miss. Auto-saves when you click away.
                                </p>
                            </div>
                        </div>

                        <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm mt-4">
                            <h3 className="font-bold text-gray-800 mb-2">How to Use V2</h3>
                            <ol className="list-decimal pl-5 space-y-2 text-sm text-gray-600">
                                <li>Select files in your workspace and choose <strong>Drawing Checker V2</strong> from the play dropdown.</li>
                                <li>Run the analysis. Results appear under the <strong>Drawing Checker V2</strong> tab (indigo theme).</li>
                                <li>Review issues — note the confidence labels (Verified / Likely / Possible) instead of percentages.</li>
                                <li>Use the <strong>feedback buttons</strong> on each issue to mark what's useful. This trains the system for your next run.</li>
                                <li>Click <strong>🔍 Drill Down</strong> on critical issues for a detailed root-cause analysis.</li>
                                <li>Download the annotated drawing to see issues marked directly on the drawing pages.</li>
                            </ol>
                        </div>

                    </div>
                );
            case 'drawing_atlas':
                return (
                    <div className="space-y-6">
                        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                            <h2 className="text-2xl font-bold text-gray-800">Play: Drawing Atlas</h2>
                            <p className="mt-2 text-gray-600 text-sm italic">AI Expert Role: <strong>Senior Engineering Data Analyst</strong></p>
                            <p className="text-gray-600 mt-3">
                                Drawing Atlas extracts a <strong>complete engineering record</strong> from any CAD PDF — title block, identification, geometry, material, process, dimensions, GD&T, notes, symbols, and BOM — using an <strong>8-stratum adversarial debate framework</strong> powered by SourceOptima Agentic AI.
                            </p>
                        </div>

                        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                            <h3 className="font-bold text-gray-800 mb-3">Pipeline Stages</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                {[
                                    { stage: 'Stage 0', title: 'Flash Classifier', desc: 'Identifies drawing type, standard, units, and projection angle.' },
                                    { stage: 'Route', title: 'Specialist Routing', desc: 'Routes to the appropriate specialist extractor based on drawing type.' },
                                    { stage: 'Phase A', title: 'Flash Extraction', desc: 'Extracts Stratum 1 (title block) and Stratum 2 (identification) fields.' },
                                    { stage: 'Phase B', title: 'Adversarial Debate', desc: 'Flash agent verifies while Pro agent deliberates — 2-3 rounds of debate.' },
                                    { stage: 'Phase 3', title: 'Geometry / Material / Process', desc: 'Extracts Strata 3-5 with per-stratum debate for quality assurance.' },
                                    { stage: 'Phase 4', title: 'Dimensions & GD&T', desc: 'Extracts Stratum 6 with forced extra round for tight tolerances.' },
                                    { stage: 'Phase 5A', title: 'Notes & Symbols', desc: 'Extracts Stratum 7 — general notes, surface finish, weld symbols.' },
                                    { stage: 'Phase 5B', title: 'BOM Extraction', desc: 'Extracts Stratum 8 — bill of materials with part numbers and quantities.' },
                                    { stage: 'Phase C', title: 'Acceptance Gates', desc: 'Validates extraction quality and determines terminal status.' },
                                ].map(s => (
                                    <div key={s.stage} className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                                        <span className="text-xs font-bold text-[#1e5045] uppercase">{s.stage}</span>
                                        <div className="font-semibold text-gray-800 text-sm">{s.title}</div>
                                        <p className="text-gray-500 text-xs mt-1">{s.desc}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                            <h3 className="font-bold text-gray-800 mb-3">8 Extraction Strata</h3>
                            <div className="overflow-x-auto">
                                <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-sm text-sm">
                                    <thead>
                                        <tr className="bg-gray-50">
                                            <th className="px-4 py-2 text-left font-medium text-gray-600">Stratum</th>
                                            <th className="px-4 py-2 text-left font-medium text-gray-600">Fields</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[
                                            { n: 'S1 — Title Block', fields: 'Drawing number, title, revision, date, company, scale, sheet size' },
                                            { n: 'S2 — Identification', fields: 'Part number, description, cage code, contract, standard, category' },
                                            { n: 'S3 — Geometry', fields: 'Overall dimensions, volume, bounding box, feature count, complexity' },
                                            { n: 'S4 — Material', fields: 'Material specification, grade, condition, coating, hardness' },
                                            { n: 'S5 — Process', fields: 'Surface finish, heat treatment, plating, machining operations' },
                                            { n: 'S6 — Dimensions & GD&T', fields: 'Toleranced dimensions, GD&T frames, datum references' },
                                            { n: 'S7 — Notes & Symbols', fields: 'General notes, surface finish symbols, weld symbols' },
                                            { n: 'S8 — BOM', fields: 'Bill of materials items, quantities, part numbers, descriptions' },
                                        ].map(s => (
                                            <tr key={s.n} className="border-t border-gray-100 hover:bg-gray-50">
                                                <td className="px-4 py-2 font-semibold text-gray-800 whitespace-nowrap">{s.n}</td>
                                                <td className="px-4 py-2 text-gray-600">{s.fields}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                            <h3 className="font-bold text-gray-800 mb-3">Adversarial Debate Framework</h3>
                            <p className="text-gray-600 text-sm">
                                Drawing Atlas uses a <strong>Flash/Pro adversarial debate</strong> at every stratum. The Flash agent extracts quickly, then the Pro agent challenges each field — checking for missing data, incorrect values, and confidence levels. This multi-round process ensures extraction quality far exceeds single-pass approaches.
                            </p>
                            <div className="mt-3 grid grid-cols-3 gap-3 text-center text-sm">
                                <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                                    <div className="font-bold text-green-800">HIGH</div>
                                    <div className="text-xs text-green-600">Verified by debate</div>
                                </div>
                                <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                                    <div className="font-bold text-amber-800">MEDIUM</div>
                                    <div className="text-xs text-amber-600">Some uncertainty</div>
                                </div>
                                <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                                    <div className="font-bold text-red-800">LOW</div>
                                    <div className="text-xs text-red-600">Needs review</div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                            <h3 className="font-bold text-gray-800 mb-3">Best Used For</h3>
                            <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                                <li>Extracting full engineering data records from CAD drawings</li>
                                <li>Building searchable databases of drawing metadata</li>
                                <li>Automated quality checks on drawing completeness</li>
                                <li>Feeding downstream plays (Tariff, Cost, Weight) with structured data</li>
                                <li>Compliance audits requiring complete drawing field extraction</li>
                            </ul>
                        </div>

                        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                            <h3 className="font-bold text-gray-800 mb-3">How to Run</h3>
                            <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600">
                                <li>Select files in your workspace and choose <strong>Drawing Atlas</strong> from the play dropdown.</li>
                                <li>Watch the <strong>terminal panel</strong> — each pipeline stage streams live (Classify → Route → Extract → Debate → Gates).</li>
                                <li>When complete, the result card shows all 8 strata with <strong>per-field confidence tiers</strong> and <strong>debate dissent highlights</strong>.</li>
                                <li>Use the <strong>Export CSV</strong> button to download the full extraction as a spreadsheet.</li>
                            </ol>
                        </div>

                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-sm">
                            <h3 className="font-bold text-blue-800 mb-1">Performance Notes</h3>
                            <p className="text-blue-900">
                                A typical run takes <strong>3–10 minutes</strong> per drawing depending on complexity and the number of debate rounds.
                                The adversarial framework uses <strong>2–3 rounds per stratum</strong>, with forced extra rounds for dimensions and GD&T.
                            </p>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="flex h-screen bg-gray-50 overflow-hidden">
            {/* Sidebar */}
            <div className="w-64 bg-white border-r border-gray-200 flex flex-col overflow-y-auto">
                <div className="p-6 border-b border-gray-200">
                    <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                        <FaBook className="text-[#1E5045]" />
                        <span>Training</span>
                    </h1>
                </div>
                <nav className="flex-1 p-4 space-y-1">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                                activeTab === tab.id 
                                ? 'bg-[#1E5045] text-white shadow-sm' 
                                : 'text-gray-600 hover:bg-gray-100'
                            }`}
                        >
                            <span className="text-lg">{tab.icon}</span>
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto p-8">
                <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-sm border border-gray-200 p-8">
                    {renderContent()}
                </div>
            </div>
        </div>
    );
};

export default TrainingDocs;
